import React from 'react';

export const MARKER_KINDS = ['city', 'town', 'village', 'castle', 'ruin', 'peak', 'dungeon', 'tower', 'site', 'bridge', 'temple', 'statue', 'spot', 'shop', 'guild', 'tavern', 'villa', 'civic', 'misc'] as const;

export type MarkerKind = (typeof MARKER_KINDS)[number];

export const DEPTHS = ['upper', 'middle', 'lower', 'all'] as const;

export type Depth = (typeof DEPTHS)[number];

export const MARKER_SIZE: Record<MarkerKind | Depth, number> = {
	city: 128,
	town: 84,
	village: 80,
	castle: 76,
	ruin: 76,
	peak: 84,
	dungeon: 84,
	tower: 72,
	site: 72,
	bridge: 96,
	temple: 44,
	statue: 46,
	spot: 36,
	shop: 40,
	guild: 40,
	tavern: 40,
	villa: 40,
	civic: 40,
	misc: 36,
	upper: 110,
	middle: 110,
	lower: 110,
	all: 110,
};

export const MARKER_LOD: Record<MarkerKind | Depth, {hideBelow: number; minSize: number; maxSize: number}> = {
	city: {hideBelow: 1.2, minSize: 7, maxSize: 22},
	town: {hideBelow: 2.6, minSize: 6, maxSize: 16},
	village: {hideBelow: 4, minSize: 5, maxSize: 14},
	castle: {hideBelow: 4, minSize: 5, maxSize: 14},
	ruin: {hideBelow: 4, minSize: 5, maxSize: 14},
	peak: {hideBelow: 4, minSize: 5, maxSize: 15},
	dungeon: {hideBelow: 4, minSize: 5, maxSize: 15},
	tower: {hideBelow: 4, minSize: 5, maxSize: 14},
	site: {hideBelow: 4, minSize: 5, maxSize: 13},
	bridge: {hideBelow: 5, minSize: 6, maxSize: 16},
	temple: {hideBelow: 6, minSize: 9, maxSize: 18},
	statue: {hideBelow: 6, minSize: 9, maxSize: 18},
	spot: {hideBelow: 6, minSize: 8, maxSize: 16},
	shop: {hideBelow: 6, minSize: 9, maxSize: 17},
	guild: {hideBelow: 6, minSize: 9, maxSize: 17},
	tavern: {hideBelow: 6, minSize: 9, maxSize: 17},
	villa: {hideBelow: 6, minSize: 9, maxSize: 17},
	civic: {hideBelow: 6, minSize: 9, maxSize: 17},
	misc: {hideBelow: 6, minSize: 8, maxSize: 16},
	upper: {hideBelow: 0, minSize: 8, maxSize: 16},
	middle: {hideBelow: 0, minSize: 8, maxSize: 16},
	lower: {hideBelow: 0, minSize: 8, maxSize: 16},
	all: {hideBelow: 0, minSize: 8, maxSize: 16},
};

export const MARKER_TITLES: Record<MarkerKind, string> = {
	city: 'Большой город',
	town: 'Город',
	village: 'Деревня',
	castle: 'Замок, крепость, твердыня',
	ruin: 'Руины',
	peak: 'Горная вершина',
	dungeon: 'Подземелье',
	tower: 'Башня',
	site: 'Примечательное место',
	bridge: 'Мост',
	temple: 'Храм, святилище',
	statue: 'Шагающая статуя',
	spot: 'Место из приключения',
	shop: 'Торговля',
	guild: 'Гильдия',
	tavern: 'Таверна',
	villa: 'Вилла',
	civic: 'Городское здание',
	misc: 'Разное',
};

export const DEPTH_TITLES: Record<Depth, string> = {
	upper: 'Верхнее Подземье',
	middle: 'Среднее Подземье',
	lower: 'Нижнее Подземье',
	all: 'Все глубины',
};

export const DEPTH_COLORS: Record<Depth, string> = {
	upper: '#b8322a',
	middle: '#2f55c8',
	lower: '#14806a',
	all: '#7d3fb5',
};

export const KIND_TITLES: Record<string, string> = {
	settlement: 'Место',
	region: 'Регион',
	realm: 'Страна, область',
	sea: 'Море, залив',
	lake: 'Озеро',
	river: 'Река',
	forest: 'Лес',
	mountains: 'Горы',
	hills: 'Холмы',
	terrain: 'Местность',
	swamp: 'Болото',
	desert: 'Пустыня',
	glacier: 'Ледник',
	island: 'Остров',
	road: 'Дорога',
	place: 'Место',
	ward: 'Часть города',
	district: 'Район',
	building: 'Здание',
	gate: 'Ворота',
	water: 'Река',
	street: 'Улица',
	'underdark-area': 'Подземье',
	'underdark-region': 'Область Подземья',
	'underdark-place': 'Место в Подземье',
};

export const isMarkerKind = (marker: string | null | undefined): marker is MarkerKind =>
	!!marker && (MARKER_KINDS as readonly string[]).includes(marker);

export const isDepth = (marker: string | null | undefined): marker is Depth =>
	!!marker && (DEPTHS as readonly string[]).includes(marker);

const INK = '#141414';
const PAPER = '#fffdf5';

export const MarkerShape: React.FC<{kind: MarkerKind | Depth; size?: number}> = ({kind, size = 16}) => (
	<svg width={size} height={size} viewBox="-1 -1 2 2" aria-hidden="true">
		<use href={`#map-m-${kind}`} x="-1" y="-1" width="2" height="2" />
	</svg>
);

export const MarkerSymbols: React.FC = () => (
	<svg width="0" height="0" style={{position: 'absolute'}} aria-hidden="true">
		<defs>
			{DEPTHS.map((depth) => (
				<pattern key={depth} id={`map-depth-${depth}`} width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
					<rect width="12" height="12" fill={DEPTH_COLORS[depth]} fillOpacity="0.16" />
					<rect width="4" height="12" fill={DEPTH_COLORS[depth]} fillOpacity="0.45" />
				</pattern>
			))}
		</defs>
		<symbol id="map-m-city" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={INK} />
			<circle r="0.8" fill={PAPER} />
			<circle r="0.6" fill={INK} />
		</symbol>
		<symbol id="map-m-town" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={PAPER} />
			<circle r="0.8" fill={INK} />
		</symbol>
		<symbol id="map-m-village" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={INK} />
			<circle r="0.72" fill={PAPER} />
		</symbol>
		<symbol id="map-m-castle" viewBox="-1 -1 2 2">
			<rect x="-0.9" y="-0.9" width="1.8" height="1.8" fill={INK} />
			<rect x="-0.62" y="-0.62" width="1.24" height="1.24" fill={PAPER} />
		</symbol>
		<symbol id="map-m-ruin" viewBox="-1 -1 2 2">
			<rect x="-0.95" y="-0.95" width="1.9" height="1.9" fill={PAPER} />
			<rect x="-0.8" y="-0.8" width="1.6" height="1.6" fill={INK} />
		</symbol>
		<symbol id="map-m-peak" viewBox="-1 -1 2 2">
			<path d="M0 -0.95 L0.98 0.8 L-0.98 0.8 Z" fill={INK} />
			<path d="M0 -0.48 L0.58 0.56 L-0.58 0.56 Z" fill={PAPER} />
		</symbol>
		<symbol id="map-m-dungeon" viewBox="-1 -1 2 2">
			<path d="M-1 -0.85 L1 -0.85 L0 0.95 Z" fill={PAPER} />
			<path d="M-0.78 -0.72 L0.78 -0.72 L0 0.68 Z" fill={INK} />
		</symbol>
		<symbol id="map-m-tower" viewBox="-1 -1 2 2">
			<path
				d="M-0.8 -0.95 H-0.45 V-0.65 H-0.18 V-0.95 H0.18 V-0.65 H0.45 V-0.95 H0.8 V-0.25 H0.55 V0.55 H0.8 V0.95 H-0.8 V0.55 H-0.55 V-0.25 H-0.8 Z"
				fill={INK}
				stroke={PAPER}
				strokeWidth="0.12"
				paintOrder="stroke"
			/>
		</symbol>
		<symbol id="map-m-site" viewBox="-1 -1 2 2">
			<path d="M-0.7 -0.7 L0.7 0.7 M0.7 -0.7 L-0.7 0.7" stroke={PAPER} strokeWidth="0.75" strokeLinecap="round" />
			<path d="M-0.7 -0.7 L0.7 0.7 M0.7 -0.7 L-0.7 0.7" stroke={INK} strokeWidth="0.45" />
		</symbol>
		<symbol id="map-m-temple" viewBox="-1 -1 2 2">
			<path d="M0 -1 C0.15 -0.15 0.15 -0.15 1 0 C0.15 0.15 0.15 0.15 0 1 C-0.15 0.15 -0.15 0.15 -1 0 C-0.15 -0.15 -0.15 -0.15 0 -1 Z" fill={PAPER} />
			<path d="M0 -0.82 C0.12 -0.12 0.12 -0.12 0.82 0 C0.12 0.12 0.12 0.12 0 0.82 C-0.12 0.12 -0.12 0.12 -0.82 0 C-0.12 -0.12 -0.12 -0.12 0 -0.82 Z" fill={INK} />
		</symbol>
		<symbol id="map-m-shop" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={PAPER} />
			<circle r="0.8" fill={INK} />
			<circle r="0.46" fill="none" stroke={PAPER} strokeWidth="0.14" />
			<circle r="0.14" fill={PAPER} />
		</symbol>
		<symbol id="map-m-guild" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={PAPER} />
			<circle r="0.8" fill={INK} />
			<path d="M0 -0.58 L0.46 -0.4 L0.42 0.1 C0.38 0.38 0.16 0.52 0 0.6 C-0.16 0.52 -0.38 0.38 -0.42 0.1 L-0.46 -0.4 Z" fill={PAPER} />
		</symbol>
		<symbol id="map-m-tavern" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={PAPER} />
			<circle r="0.8" fill={INK} />
			<path d="M-0.42 -0.42 H0.2 V0.48 H-0.42 Z" fill={PAPER} />
			<path d="M0.2 -0.22 H0.42 V0.24 H0.2" fill="none" stroke={PAPER} strokeWidth="0.13" />
		</symbol>
		<symbol id="map-m-villa" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={PAPER} />
			<circle r="0.8" fill={INK} />
			<path d="M0 -0.56 L0.56 -0.04 H0.38 V0.5 H-0.38 V-0.04 H-0.56 Z" fill={PAPER} />
		</symbol>
		<symbol id="map-m-civic" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={PAPER} />
			<circle r="0.8" fill={INK} />
			<path d="M0 -0.58 L0.54 -0.26 H-0.54 Z M-0.42 -0.16 H-0.24 V0.32 H-0.42 Z M-0.09 -0.16 H0.09 V0.32 H-0.09 Z M0.24 -0.16 H0.42 V0.32 H0.24 Z M-0.54 0.38 H0.54 V0.52 H-0.54 Z" fill={PAPER} />
		</symbol>
		<symbol id="map-m-misc" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={PAPER} />
			<circle r="0.8" fill={INK} />
			<circle r="0.32" fill={PAPER} />
		</symbol>
		<symbol id="map-m-statue" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={INK} />
			<circle r="0.8" fill={PAPER} />
			<path d="M0 -0.62 L0.15 -0.2 L0.6 -0.19 L0.24 0.08 L0.37 0.52 L0 0.26 L-0.37 0.52 L-0.24 0.08 L-0.6 -0.19 L-0.15 -0.2 Z" fill={INK} />
		</symbol>
		<symbol id="map-m-spot" viewBox="-1 -1 2 2">
			<circle r="0.97" fill={PAPER} />
			<circle r="0.75" fill={INK} />
		</symbol>
		<symbol id="map-m-bridge" viewBox="-1 -1 2 2">
			<rect x="-0.95" y="-0.5" width="1.9" height="1" fill={PAPER} />
			<rect x="-0.85" y="-0.4" width="1.7" height="0.8" fill={INK} />
			<rect x="-0.45" y="-0.25" width="0.9" height="0.5" fill={PAPER} />
		</symbol>
		{DEPTHS.map((depth) => (
			<symbol key={depth} id={`map-m-${depth}`} viewBox="-1 -1 2 2">
				<circle r="0.97" fill={PAPER} />
				<circle r="0.78" fill={DEPTH_COLORS[depth]} />
			</symbol>
		))}
	</svg>
);
